import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImageCarousel, {
    ProductImageCarouselStyles,
} from "@insite/content-library/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Badge from "@insite/mobius/Badge";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconMemo, IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import ZoomIn from "@insite/mobius/Icons/ZoomIn";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { comingSoonDiscontinueDates } from "@tKgpco/Common/commonFunctions";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line no-use-before-define
type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    properties: state.context.settings.properties,
    isAuthenticated: state.context.session?.isAuthenticated && !state.context.session?.isGuest,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};
const backToTopClickHandler = () => {
    const header = document.getElementById("specsHeader");
    if (header) {
        const { top } = header.getBoundingClientRect();
        window.scrollTo({
            top: top + window.pageYOffset - 100,
            behavior: "smooth",
        });
    }
};
export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;
    backToTopButtonPdp?: ButtonPresentationProps;
    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    imgWrapper?: InjectableCss;
    zoomIcon?: InjectableCss;
    scrollFixedForCart?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    badgeImgWrapper?: InjectableCss;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
    },
    imgWrapper: {
        css: css`
            border: 1px solid #e1e7ed;
            border-radius: 8px;
            position: relative;
            padding: 5px;
        `,
    },
    zoomIcon: {
        css: css`
            position: absolute;
            bottom: 15px;
            right: 21px;
            cursor: pointer;
        `,
    },
    backToTopButtonPdp: {
        css: css`
            display: none;
        `,
    },
    scrollFixedForCart: {
        css: css`
            min-width: auto;
            min-height: auto;
            padding: 10px 11px;
            position: fixed;
            right: 15px;
            bottom: 10px;
            background: transparent linear-gradient(180deg, #44a8f7 0%, #1e75ba 100%) 0% 0% no-repeat;
            border: 0;
            border-radius: 50%;
            display: block;
            z-index: 9;
            > span {
                height: 20px;
                width: 20px;
                svg {
                    height: 20px;
                    width: 20px;
                    stroke: #fff;
                }
            }
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    badgeImgWrapper: {
        css: css`
            position: absolute;
            left: 50%;
            top: 50%;
            width: 25%;
            height: 28%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    properties,
    isAuthenticated
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const [offset, setOffset] = useState(0);
    const [footerPosition, setFooterPosition] = useState(false);
    const { comingSoonImagePath, discontinuedImagePath } = properties;
    const { isComingSoon, isDiscontinued }  = comingSoonDiscontinueDates(productContext.product);
    useEffect(() => {
        document.addEventListener("scroll", function (e) {
            const check = window.parent.document.getElementById("siteIFrame");
            const summaryContainer = document.getElementById("cartWrapper");
            const footer = window.parent.document.getElementsByTagName("footer");
            const elementHeight = summaryContainer?.offsetHeight ? summaryContainer?.offsetHeight : 42;
            if (window.pageYOffset + window.innerHeight - elementHeight < footer[0]?.offsetTop) {
                setFooterPosition(true);
            } else {
                setFooterPosition(false);
            }
            if (!check) {
                setOffset(window.pageYOffset);
            } else {
                setOffset(2000);
            }
        });
    }, []);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    const { product } = productContext;

    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.imgWrapper} id="specsHeader">
                {!showOverlayBadge && (
                    <ProductPrimaryImage product={product} image={image} extendedStyles={styles.primaryImage} />
                )}
                {showOverlayBadge && overlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper}  ref={badgeAreaWrapperRef}>
                        <ProductPrimaryImage product={product} image={image} extendedStyles={styles.primaryImage} />
                        <StyledWrapper {...overlayBadgeWrapperStyles}  onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                <Icon src={ZoomIn} {...styles.zoomIcon} onClick={imageClickHandler} />
            </StyledWrapper>
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={styles.fullPrimaryImage}
                />
                {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                    />
                )}
            </Modal>
            <Button
                id="cartWrapper"
                onClick={backToTopClickHandler}
                {...styles.backToTopButtonPdp}
                {...(offset > 100 && footerPosition === true ? { ...styles.scrollFixedForCart } : "")}
            >
                <Icon src={ChevronUp} />
            </Button>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
